import { useState, useEffect, useRef, useCallback } from 'react'
import { Search, MapPin, Phone, Building, ChevronRight, X, Star, Edit3, User } from 'lucide-react'
import { useI18n } from '../i18n'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'

/**
 * ExternalDoctorLookup — Google Places (New API) powered doctor/practice search
 * Uses AutocompleteSuggestion + Place.fetchFields (post-March 2025 API)
 */
export default function ExternalDoctorLookup({ savedDoctor, onSave, specialty, sendVariant = false }) {
  const { t } = useI18n()
  const inputRef = useRef(null) // v234.2.0: focus target for the prominent send-variant search button
  const [isExpanded, setIsExpanded] = useState(!savedDoctor)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [selectedPlace, setSelectedPlace] = useState(savedDoctor || null)
  const [error, setError] = useState(null)
  const [apiReady, setApiReady] = useState(false)
  
  const searchTimeoutRef = useRef(null)

  // Check if new Places API is available
  useEffect(() => {
    const checkApi = () => {
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        setApiReady(true)
        return true
      }
      return false
    }

    if (!checkApi()) {
      const interval = setInterval(() => {
        if (checkApi()) clearInterval(interval)
      }, 500)
      const timeout = setTimeout(() => clearInterval(interval), 10000)
      return () => { clearInterval(interval); clearTimeout(timeout) }
    }
  }, [])

  // Debounced search using new AutocompleteSuggestion API
  const handleSearch = useCallback((value) => {
    setQuery(value)
    setError(null)

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    
    if (value.length < 3) {
      setResults([])
      setIsSearching(false)
      return
    }

    setIsSearching(true)
    searchTimeoutRef.current = setTimeout(async () => {
      if (!apiReady) {
        setError(t('errors2.searchLoading'))
        setIsSearching(false)
        return
      }

      try {
        // Try with medical type restrictions first
        const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: value,
          includedPrimaryTypes: ['doctor', 'dentist', 'hospital', 'physiotherapist', 'pharmacy'],
          includedRegionCodes: ['us'],
          language: 'en',
        })

        if (suggestions?.length > 0) {
          setResults(suggestions.slice(0, 5).map(s => {
            const pred = s.placePrediction
            const name = pred.mainText?.text || ''
            const fullText = pred.text?.text || ''
            // Extract address from fullText by stripping the name prefix
            const addressFromFull = fullText.startsWith(name) 
              ? fullText.slice(name.length).replace(/^[\s,]+/, '') 
              : pred.secondaryText?.text || ''
            return {
              placeId: pred.placeId,
              name,
              address: addressFromFull || pred.secondaryText?.text || '',
              fullText,
            }
          }))
        } else {
          setResults([])
        }
      } catch (err) {
        console.warn('[DoctorLookup] Typed search failed, trying general:', err)
        try {
          // Fallback: general search with "doctor" appended
          const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
            input: `${value} doctor`,
            includedRegionCodes: ['us'],
            language: 'en',
          })
          if (suggestions?.length > 0) {
            setResults(suggestions.slice(0, 5).map(s => {
              const pred = s.placePrediction
              const name = pred.mainText?.text || ''
              const fullText = pred.text?.text || ''
              const addressFromFull = fullText.startsWith(name) 
                ? fullText.slice(name.length).replace(/^[\s,]+/, '') 
                : pred.secondaryText?.text || ''
              return {
                placeId: pred.placeId,
                name,
                address: addressFromFull || pred.secondaryText?.text || '',
                fullText,
              }
            }))
          } else {
            setResults([])
          }
        } catch (fallbackErr) {
          console.warn('[DoctorLookup] Fallback also failed:', fallbackErr)
          setResults([])
          setError(t('errors2.searchUnavailable'))
        }
      }
      setIsSearching(false)
    }, 400)
  }, [apiReady])

  // Get full place details using new Place.fetchFields API
  const selectPlace = async (place) => {
    try {
      const placeObj = new google.maps.places.Place({ id: place.placeId })
      
      await placeObj.fetchFields({
        fields: ['displayName', 'formattedAddress', 'nationalPhoneNumber', 'internationalPhoneNumber', 'rating', 'websiteURI', 'googleMapsURI', 'location', 'types'],
      })

      const doctorData = {
        name: placeObj.displayName || place.name,
        address: placeObj.formattedAddress || place.address,
        phone: placeObj.nationalPhoneNumber || placeObj.internationalPhoneNumber || null,
        rating: placeObj.rating || null,
        website: placeObj.websiteURI || null,
        googleMapsUrl: placeObj.googleMapsURI || null,
        placeId: place.placeId,
        latitude: placeObj.location?.lat(),
        longitude: placeObj.location?.lng(),
        source: 'google_places',
        savedAt: new Date().toISOString(),
      }

      setSelectedPlace(doctorData)
      setResults([])
      setQuery('')
      onSave?.(doctorData)
      setIsExpanded(false)
      console.log('[DoctorLookup] Saved doctor, has source:', !!doctorData.source) // PROD-903: the source only, not the record
    } catch (err) {
      console.warn('[DoctorLookup] fetchFields failed, saving basic data:', err)
      const doctorData = {
        name: place.name,
        address: place.address,
        fullAddress: place.fullText,
        placeId: place.placeId,
        source: 'google_places',
        savedAt: new Date().toISOString(),
      }
      setSelectedPlace(doctorData)
      setResults([])
      setQuery('')
      onSave?.(doctorData)
      setIsExpanded(false)
    }
  }

  const clearSelection = () => {
    setSelectedPlace(null)
    setIsExpanded(true)
    onSave?.(null)
  }

  // ===== SAVED STATE — compact card =====
  if (selectedPlace && !isExpanded) {
    return (
      <div className="bg-white rounded-2xl border-[1.5px] border-green-200 overflow-hidden">
        <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-green-500 bg-green-50">
          <Building size={15} className="text-green-600" />
          <h4 className="text-[12px] font-bold uppercase tracking-[0.5px] text-green-700">{t('doctorLookup.yourDoctorsOffice')}</h4>
          <div className="ml-auto flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(true)}
              className="p-1 text-green-600 active:text-green-800"
            >
              <Edit3 size={13} />
            </button>
            <button
              onClick={clearSelection}
              className="p-1 text-red-400 active:text-red-600"
            >
              <X size={13} />
            </button>
          </div>
        </div>
        <div className="px-3.5 py-3 space-y-1.5">
          <p className="text-[13px] font-bold" style={{ color: NAVY }}>{selectedPlace.name}</p>
          {selectedPlace.address && (
            <div className="flex items-start gap-1.5">
              <MapPin size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11.5px] text-gray-500 leading-snug">{selectedPlace.address}</p>
            </div>
          )}
          {selectedPlace.phone && (
            <div className="flex items-center gap-1.5">
              <Phone size={12} className="text-gray-400" />
              <p className="text-[11.5px] text-gray-500">{selectedPlace.phone}</p>
            </div>
          )}
          {selectedPlace.rating && (
            <div className="flex items-center gap-1">
              <Star size={12} className="text-[#46A8F9]" fill="#46A8F9" />
              <span className="text-[11px] font-semibold text-[#46A8F9]">{selectedPlace.rating}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ===== SEARCH STATE =====
  return (
    <div className="bg-white rounded-2xl border-[1.5px] border-gray-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-l-[3px] border-l-[#46A8F9] bg-[#E9F8FF]">
        <Building size={15} className="text-[#46A8F9]" />
        <h4 className="text-[13px] font-bold text-[#15354F]">
          {sendVariant
            ? (t('doctorLookup.addDoctorSending') || "Add the doctor you're sending to")
            : (t('doctorLookup.addDoctorsOffice') || "Add your doctor's office")}
        </h4>
        <span className="ml-auto text-[9px] font-semibold text-gray-400">{t('doctorLookup.optional')}</span>
      </div>

      <div className="p-3.5 space-y-3">
        <p className="text-[11.5px] text-gray-400 leading-relaxed">
          {sendVariant
            ? (t('doctorLookup.sendDesc') || "Search and we'll add their name, address and phone to your visit-prep document — then you can send the case.")
            : (t('doctorLookup.lookupDesc') || "Find the practice and we'll add their name, address and phone to your visit-prep document.")}
        </p>

        {/* Search Input — send variant pairs it with a prominent blue search button */}
        <div className={sendVariant ? 'flex items-center gap-2.5' : ''}>
          <div className="relative flex-1">
            <div className="absolute left-3 top-1/2 -translate-y-1/2">
              {isSearching ? (
                <OrellaSpinner size={16} />
              ) : sendVariant ? (
                <User size={16} className="text-gray-400" />
              ) : (
                <Search size={16} className="text-gray-400" />
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={sendVariant
                ? (t('doctorLookup.searchPlaceholderSend') || 'Your doctor or practice…')
                : 'Search doctor or practice name...'}
              className="w-full pl-10 pr-9 py-3 rounded-xl border-[1.5px] border-gray-200 text-[13px] placeholder-gray-300 focus:outline-none focus:border-[#46A8F9] transition-colors"
              style={{ color: NAVY }}
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setResults([]); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-300 active:text-gray-500"
              >
                <X size={14} />
              </button>
            )}
          </div>
          {sendVariant && (
            <button
              type="button"
              onClick={() => inputRef.current?.focus()}
              aria-label={t('doctorLookup.search') || 'Search'}
              className="w-[46px] h-[46px] rounded-xl flex items-center justify-center flex-shrink-0 active:scale-95 transition-transform"
              style={{ backgroundColor: '#46A8F9', boxShadow: '0 4px 10px rgba(70,168,249,0.35)' }}
            >
              <Search size={18} className="text-white" />
            </button>
          )}
        </div>

        {error && (
          <p className="text-[11px] text-amber-600">{error}</p>
        )}

        {/* Search Results */}
        {results.length > 0 && (
          <div className="space-y-1 max-h-[240px] overflow-y-auto -mx-1 px-1">
            {results.map((place, i) => (
              <button
                key={place.placeId || i}
                onClick={() => selectPlace(place)}
                className="w-full text-left p-3 rounded-xl border-[1.5px] border-gray-100 active:border-[#46A8F9] active:bg-[#E9F8FF] transition-colors flex items-start gap-2.5"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={16} className="text-[#46A8F9]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12.5px] font-semibold truncate" style={{ color: NAVY }}>
                    {place.name}
                  </p>
                  <p className="text-[11px] text-gray-400 truncate">{place.address}</p>
                </div>
                <ChevronRight size={14} className="text-gray-300 flex-shrink-0 mt-1" />
              </button>
            ))}
          </div>
        )}

        {query.length >= 3 && !isSearching && results.length === 0 && (
          <p className="text-[11px] text-gray-400 text-center py-2">
            {t('doctorLookup.noResultsTryAgain')}
          </p>
        )}
      </div>
    </div>
  )
}
